import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Cache } from 'aws-amplify';

const STORAGE_KEY = 'view_as_scope';

export type ViewAsRole = 'distiadmin' | 'clientadmin' | 'campusadmin';

export interface ViewAsScope {
	enabled: boolean;
	role: ViewAsRole;
	distiId?: string | null;
	clientId?: string | null;
	campusId?: string | null;
	/** Display name for distributor (stored for tooltip) */
	distiName?: string | null;
	/** Display name for client (stored for tooltip) */
	clientName?: string | null;
	/** Display name for campus (stored for tooltip) */
	campusName?: string | null;
}

@Injectable({
	providedIn: 'root'
})
export class ViewAsScopeService {
	private readonly originalCacheGetItem: (key: string) => unknown;
	private readonly scopeSubject = new BehaviorSubject<ViewAsScope | null>(this.getScope());

	readonly scope$: Observable<ViewAsScope | null> = this.scopeSubject.asObservable();

	constructor() {
		// Capture original Cache.getItem before any patches run
		this.originalCacheGetItem = Cache.getItem.bind(Cache);
	}

	/**
	 * Check if View As scope is enabled and has valid configuration
	 */
	isEnabled(): boolean {
		const scope = this.getScope();
		return scope !== null && scope.enabled && !!scope.role;
	}

	/**
	 * Check if the current user can enable View As (super admins only)
	 */
	canEnable(): boolean {
		const userAttr = this.getOriginalUserAttributes();
		return userAttr && userAttr['custom:role'] === 'sadmin';
	}

	/**
	 * Get the stored View As scope from localStorage
	 */
	getScope(): ViewAsScope | null {
		try {
			const stored = localStorage.getItem(STORAGE_KEY);
			if (!stored) {
				return null;
			}
			const parsed = JSON.parse(stored) as ViewAsScope;
			if (!parsed || typeof parsed.enabled !== 'boolean') {
				return null;
			}
			return parsed;
		} catch {
			return null;
		}
	}

	/**
	 * Set View As scope. Only allows when canEnable() is true.
	 */
	setScope(scope: ViewAsScope): void {
		if (!this.canEnable()) {
			return;
		}
		try {
			localStorage.setItem(STORAGE_KEY, JSON.stringify(scope));
			this.scopeSubject.next(scope);
		} catch {
			// Silently fail if localStorage is unavailable
		}
	}

	/**
	 * Clear View As scope
	 */
	clearScope(): void {
		try {
			localStorage.removeItem(STORAGE_KEY);
			this.scopeSubject.next(null);
		} catch {
			// Silently fail
		}
	}

	/**
	 * Get effective user attributes with View As scope overrides applied.
	 * Merges real userAttr with scope overrides for role and entity IDs.
	 */
	getEffectiveUserAttributes(): Record<string, string> {
		const userAttr = this.getOriginalUserAttributes() as Record<string, string> | null;
		if (!userAttr) {
			return {};
		}

		const scope = this.getScope();
		if (!scope || !scope.enabled || !scope.role) {
			return { ...userAttr };
		}

		const overridden = { ...userAttr };
		overridden['custom:role'] = scope.role;

		overridden['custom:disti_id'] =
			scope.distiId !== null && scope.distiId !== undefined ? String(scope.distiId) : '';
		overridden['custom:client_id'] =
			scope.clientId !== null && scope.clientId !== undefined ? String(scope.clientId) : '';
		overridden['custom:campus_id'] =
			scope.campusId !== null && scope.campusId !== undefined ? String(scope.campusId) : '';

		return overridden;
	}

	/**
	 * Get tooltip text for View As scope (e.g. "Viewing as Client Admin – Acme Corp")
	 * Uses stored names when available, falls back to IDs.
	 */
	getViewAsTooltipText(): string {
		const scope = this.getScope();
		if (!scope || !scope.enabled || !scope.role) {
			return '';
		}
		const roleLabels: Record<string, string> = {
			distiadmin: 'Partner Admin',
			clientadmin: 'Client Admin',
			campusadmin: 'Campus Admin'
		};
		const roleLabel = roleLabels[scope.role] || scope.role;
		const parts: string[] = [roleLabel];
		if (scope.distiId) {
			parts.push(scope.distiName ? `Partner: ${scope.distiName}` : `Partner: ${scope.distiId}`);
		}
		if (scope.clientId) {
			parts.push(scope.clientName ? `Client: ${scope.clientName}` : `Client: ${scope.clientId}`);
		}
		if (scope.campusId) {
			parts.push(scope.campusName ? `Campus: ${scope.campusName}` : `Campus: ${scope.campusId}`);
		}
		return `Viewing as ${parts.join(' – ')}`;
	}

	/**
	 * Get original user attributes without any overrides.
	 * Use this when you need the real user data (e.g. to show View As controls only to super admins).
	 */
	getOriginalUserAttributes(): Record<string, string> | null {
		const value = this.originalCacheGetItem('userAttr');
		return value && typeof value === 'object' ? (value as Record<string, string>) : null;
	}
}
